import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { DateUtil } from '../../common-app/util/date-util';
import { SharedService } from '../../common-app/service/shared.service';
import { Constants } from '../../constants/constants';
import { Kvaw0700Service } from '../../service/kvaw0700.service';
import { Kvaw0700Vo } from '../../service/kvaw0700.vo';
import { OldReportViewModel, Kvap0600ViewModel, YearMonthViewModel } from './kvap0600.view-model';

@Component({
  templateUrl: './kvap0600.component.html',
  providers: [Kvaw0700Service],
})

export class Kvap0600Component implements OnInit {

  /** 画面から参照するViewModel */
  vModel: Kvap0600ViewModel;

  /** 画面から参照する店舗名称 */
  storeName: string;

  /** ソート順を表すフラグ */
  private isDispOrderAscending = true;

  /** クライアント端末の現在日時 */
  private now: Date;

  constructor(
    private location: Location,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private sharedService: SharedService,
    private kvaw0700Service: Kvaw0700Service,
  ) { }

  ngOnInit() {
    this.now = new Date();
    let storeCode: string;
    const dateFrom: string = DateUtil.formatYYYYMMDD(DateUtil.adjustWithMonths(this.now, -13));
    const dateTo: string = DateUtil.formatYYYYMMDD(this.now);
    this.activatedRoute.queryParams.subscribe(param => {
      storeCode = param['storeCode'];
    });

    // 過去日報一覧情報取得処理の呼出
    this.searchOldReportListObservable(storeCode, dateFrom, dateTo).subscribe((report) => {
      this.createYearMonthViewModel();
      this.storeName = report.oldReportList[0].storeName;
    });
  }

  /**
   * 過去日報一覧情報を取得する。
   * @param storeCode 相手先コード
   * @param dateFrom 訪問年月日（From）
   * @param dateTo 訪問年月日（To）
   */
  private searchOldReportListObservable(storeCode, dateFrom, dateTo): Observable<Kvaw0700Vo> {
    return this.kvaw0700Service.searchOldReportList(storeCode, dateFrom, dateTo)
      .map(responseVo => {
        // 表示用に値を変換
        this.vModel = {};
        this.vModel.oldReportList = responseVo.oldReportList
          .map(report => <OldReportViewModel>{
            groupCompanyCode: report.groupCompanyCode,
            storeCode: report.storeCode,
            storeName: report.storeName,
            deliveryDate: report.deliveryDate,
            receptionDate: report.receptionDate,
            receptionTime: report.receptionTime,
            shimeiCode: report.shimeiCode,
            storeFrontReportContent: report.storeFrontReportContent,
            businessDiscussionReportContent: report.businessDiscussionReportContent,
            rivalryInfoReportContent: report.rivalryInfoReportContent,
            etcMemoReportContent: report.etcMemoReportContent,
            photoFilePath: report.photoFilePath,
            shareInfoType: report.shareInfoType,
          });
        return responseVo;
      });
  }

  /**
   * 表示用のViewModelを生成する。
   */
  private createYearMonthViewModel() {
    this.vModel.oldReportList.forEach(report => {
      report.formattedReceptionDate = DateUtil.formatMDddd(report.receptionDate);
      report.formattedReceptionTime =
        report.receptionTime && report.receptionTime.length > 4 ?
          report.receptionTime.substring(0, 2) + ':' + report.receptionTime.substring(2, 4) :
          report.receptionTime;
      report.formattedStoreFrontText = this.roundText(report.storeFrontReportContent);
      report.formattedBusinessDiscussionText = this.roundText(report.businessDiscussionReportContent);
      report.yearMonthLabel = DateUtil.formatYYYYMMM(report.receptionDate);
      report.timeMillis = DateUtil.parseYYYYMMDDHHmmss(report.receptionDate + report.receptionTime).getTime();
    });

    // アコーディオンに表示する13ヶ月分のリストを作成する（年月の昇順）。
    this.vModel.yearMonthList = [];
    for (let i = -13; i <= 0; i++) {
      const ym: YearMonthViewModel = {};
      const adjustedDate: Date = DateUtil.adjustWithMonths(this.now, i);
      ym.yearMonthLabel = DateUtil.formatYYYYMMM(adjustedDate);
      ym.timeMillis = adjustedDate.getTime();
      ym.isOpened = false;
      this.vModel.yearMonthList.push(ym);
    }

    // 各月のオブジェクトに報告を追加する（受信日時の昇順）。
    this.vModel.yearMonthList.forEach(ym => {
      ym.oldReportList = this.vModel.oldReportList.filter(report => {
        return ym.yearMonthLabel === report.yearMonthLabel;
      });
      ym.oldReportList.sort((rep1, rep2) => {
        return rep1.timeMillis - rep2.timeMillis;
      });
    });
  }

  /**
   * 文字列を丸める。
   * @param text 対象文字列
   */
  private roundText(text: string): string {
    const maxTextLength = 24;
    if (!text) {
      return '';
    } else if (text.length <= maxTextLength) {
      return text;
    } else {
      return text.substring(0, maxTextLength - 1) + '…';
    }
  }

  /**
   * ソート順を変更する。
   */
  changeSort() {
    if (this.isDispOrderAscending) {
      this.vModel.yearMonthList.sort((ym1, ym2) => {
        return ym2.timeMillis - ym1.timeMillis;
      });

      this.vModel.yearMonthList.forEach(list => {
        list.oldReportList.sort((rep1, rep2) => {
          return rep2.timeMillis - rep1.timeMillis;
        });
      });
      this.isDispOrderAscending = false;
    } else {
      this.vModel.yearMonthList.sort((ym1, ym2) => {
        return ym1.timeMillis - ym2.timeMillis;
      });

      this.vModel.yearMonthList.forEach(list => {
        list.oldReportList.sort((rep1, rep2) => {
          return rep1.timeMillis - rep2.timeMillis;
        });
      });
      this.isDispOrderAscending = true;
    }
  }

  /**
   * 前画面に遷移する。
   */
  back() {
    this.location.back();
  }

  /**
   * 過去日報詳細画面に遷移する。
   * @param report 過去日報ViewModel
   */
  navigateToDetail(report: OldReportViewModel) {
    this.sharedService.set(Constants.SHARED_DATA_SAVE_KEY_OLD_REPORT_PARAM, report);
    this.router.navigate(['/kvap0700']);
  }
}



// WEBPACK FOOTER //
// ./src/app/component/kvap0600/kvap0600.component.ts